import {observer} from 'mobx-react-lite';
import {TemplateDialogProps} from '../../../../entity/props/title-template/template-dialog-props';
import React, {useCallback, useEffect, useState} from 'react';
import {ManageMode} from '../../../../entity/enums/manage-mode';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Skeleton from 'react-loading-skeleton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {TemplateType} from '../../../../entity/enums/template-type';
import {useService} from '../../../../infrastructure/service/service';
import {Section} from '../../../../entity/resource/section';
import {Document} from '../../../../entity/resource/document';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {TitleTemplateRequest} from '../../../../entity/resource/title-template-request';
import {State} from '../../../../entity/model/state';
import {County} from '../../../../entity/model/county';
import {CloneTitleTemplateDto} from '../../../../entity/dto/clone-title-template-dto';
import {User} from '../../../../entity/model/user';
import {DropdownOption} from '../../../../entity/core/dropdown-option';
import {DocumentDto, SectionDto, TitleTemplateDto} from '../../../../entity/dto/title-template-dto';
import {toast} from 'react-toastify';
import {router} from '../../../router/Routes';
import {PageOption} from '../../../../entity/enums/page-option';
import {Title} from '../../../../entity/model/title';
import LoadingComponent from '../widget/LoadingComponent'
import {CloudUpload} from '@mui/icons-material';
import {useDropzone} from 'react-dropzone';
import {Project} from '../../../../entity/model/project';

import '../../../../assets/css/title-template/template-dialog.css';
import '../../../../assets/css/style.css';

const TemplateDialog = (props: TemplateDialogProps): JSX.Element => {
  const {countyService, stateService, titleService, titleTemplateService, userService, documentService, projectService} = useService();

  const [states, setStates] = useState<State[]>([]);
  const [stateOptions, setStateOptions] = useState<DropdownOption[]>([]);
  const [counties, setCounties] = useState<County[]>([]);
  const [countyOptions, setCountyOptions] = useState<DropdownOption[]>([]);
  const [templateDetails, setTemplateDetails] = useState<any[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectOptions, setProjectOptions] = useState<DropdownOption[]>([]);
  const [titleOptions, setTitleOptions] = useState<DropdownOption[]>([]);
  const [templateDetailsAccordion, setTemplateDetailsAccordion] = useState<boolean>(true);
  const [sectionAccordion, setSectionAccordion] = useState<boolean>(true);
  const [importAccordion, setImportAccordion] = useState<boolean>(true);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [expandedSection, setExpandedSection] = useState<number | null>(null);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [selectedSections, setSelectedSections] = useState<Map<string, string[]>>(new Map());
  const [currentUser, setCurrentUser] = useState<User>();
  const [templateName, setTemplateName] = useState<string>('');
  const [templateType, setTemplateType] = useState<TemplateType>(TemplateType.ORGANIZATION);
  const [templateCounty, setTemplateCounty] = useState<string>('');
  const [templateTmsNumbers, setTemplateTmsNumbers] = useState<string>('');
  const [templateState, setTemplateState] = useState<string>('');
  const [templateAddress, setTemplateAddress] = useState<string>('');
  const [templateSubdivision, setTemplateSubdivision] = useState<string>('');
  const [importProject, setImportProject] = useState<string>();
  const [titles, setTitles] = useState<Title[]>([]);
  const [importTitle, setImportTitle] = useState<string>();
  const [title, setTitle] = useState<Title>();
  const [loading, setLoading] = useState<boolean>(false);
  const [documentCount, setDocumentCount] = useState<number>(0);
  const [documentIndex, setDocumentIndex] = useState<number>(0);
  const [uploading, setUploading] = useState<boolean>(false);
  const [updatedSection, setUpdatedSection] = useState<Section>();
  const [updatedDocuments, setUpdatedDocuments] = useState<Map<string, DocumentDto[]>>(new Map());
  const [loadingTitles, setLoadingTitles] = useState<boolean>(false);

  const submitButtonMap = new Map([
    [ManageMode.DOWNLOAD, 'Downloading'],
    [ManageMode.TITLE_DOWNLOAD, 'Downloading'],
    [ManageMode.CLONE, 'Cloning'],
    [ManageMode.UPDATE, 'Updating'],
    [ManageMode.QUICK_UPDATE, 'Updating'],
    [ManageMode.IMPORT, 'Importing'],
    [ManageMode.EXPORT, 'Exporting']
  ]);

  const onDrop = useCallback(async (acceptedFiles: any): Promise<void> => {
    if (!updatedSection)
      return;

    const pdfFiles = acceptedFiles.filter((file: any): boolean => file.type === 'application/pdf');

    if (pdfFiles.length === 0) {
      toast.warning('Only PDF files are allowed.');
      return;
    }

    setUploading(true);
    setDocumentCount(pdfFiles.length);

    const batchSize = 3;
    const batches = [];

    for (let i = 0; i < pdfFiles.length; i += batchSize)
      batches.push(pdfFiles.slice(i, i + batchSize));

    for (const [batchIndex, batch] of batches.entries()) {
      await Promise.all(
        batch.map(async (file: any, index: number): Promise<void> => {
          const globalIndex = batchIndex * batchSize + index + 1;
          setDocumentIndex(globalIndex);

          const response = await documentService.uploadFile(props.titleTemplate?.createdByOrganizationID ?? '', file, undefined, undefined, undefined, props.titleTemplate?.id);
          if (response && updatedSection) {
            setUpdatedDocuments((prevState: Map<string, DocumentDto[]>): Map<string, DocumentDto[]> => {
              const updatedDocuments = new Map(prevState);
              const currentDocuments = updatedDocuments.get(updatedSection.id) || [];

              updatedDocuments.set(updatedSection.id, [...currentDocuments, response]);

              return updatedDocuments;
            });

            setSelectedSections((prevState: Map<string, string[]>): Map<string, string[]> => {
              const updatedSelectedSections = new Map(prevState);
              const currentDocumentIds = updatedSelectedSections.get(updatedSection.id) || [];

              if (!currentDocumentIds.includes(response.id))
                updatedSelectedSections.set(updatedSection.id, [...currentDocumentIds, response.id]);

              return updatedSelectedSections;
            });
          }
        })
      );
    }

    setUploading(false);
  }, [updatedSection]);

  const { getRootProps, getInputProps } = useDropzone({onDrop, useFsAccessApi: false});

  useEffect(() => {
    const init = async(): Promise<void> => {
      setLoading(true);

      const user = await userService.getCurrentUser();
      setCurrentUser(user);

      const states = await stateService.getStates();
      const state = states.find((state: State): boolean => state.id === (props.projectTitle ? props.projectTitle.stateID : props.titleTemplate?.stateID ?? ''));
      setStates(states);
      setStateOptions(states.map((state: State): DropdownOption => ({key: state.id, text: state.shortName, value: state.id})));

      const counties = await countyService.getCounties();
      const county = counties.find((county: County): boolean => county.id === (props.projectTitle ? props.projectTitle.countyID : props.titleTemplate?.countyID ?? ''));
      setCounties(counties);
      setCountyOptions(counties.map((county: County): DropdownOption => ({key: county.id, text: county.name, value: county.id})));

      setTemplateDetails([
        { label: 'Type', value: props.projectTitle ? TemplateType.ORGANIZATION : props.mode === ManageMode.CLONE && props.titleTemplate?.isPublic ? TemplateType.ORGANIZATION : props.titleTemplate?.isPublic ? TemplateType.PUBLIC : TemplateType.ORGANIZATION },
        { label: 'Name', value: props.projectTitle ? props.orderNumber : props.titleTemplate?.name ?? '' },
        { label: 'County', value: county?.name ?? '' },
        { label: 'TMS/ Parcel Numbers', value: props.projectTitle ? props.projectTitle.tmsParcelNumbers.join(', ') : props.titleTemplate?.tmsParcelNumbers.join(', ') ?? '' },
        { label: 'State', value: state?.shortName ?? '' },
        { label: 'Address', value: props.projectTitle ? props.projectTitle.address : props.titleTemplate?.address ?? '' },
        { label: 'Sub Division', value: props.projectTitle ? props.projectTitle.subdivision : props.titleTemplate?.subdivision ?? '' }
      ]);

      if (props.mode === ManageMode.IMPORT && !props.importTitle) {
        let projects = await projectService.getProjects(user?.organizationID ?? '', userService.isAdminUser());
        projects = projects.filter((project: Project): boolean => projectService.isPermittedProject(project.id));

        setProjects(projects);
        setProjectOptions(projects.map((project: Project): DropdownOption => ({key: project.id, text: project.orderNumber, value: project.id})));
      }

      if (props.mode === ManageMode.TITLE_DOWNLOAD || props.mode === ManageMode.EXPORT)
        setTitle(titleService.getTitle());

      setSelectAll(true);
      handleSelectAll();

      setTemplateName(`${props.projectTitle ? props.orderNumber : props.titleTemplate?.name}${props.mode === ManageMode.CLONE ? ' - Clone' : ''}`);
      setTemplateType(props.titleTemplate?.isPublic ? TemplateType.PUBLIC : TemplateType.ORGANIZATION);
      setTemplateCounty(props.titleTemplate?.countyID ?? '');
      setTemplateTmsNumbers(props.titleTemplate?.tmsParcelNumbers.join(', ') ?? '');
      setTemplateState(props.titleTemplate?.stateID ?? '');
      setTemplateAddress(props.titleTemplate?.address ?? '');
      setTemplateSubdivision(props.projectTitle ? props.projectTitle.subdivision : props.titleTemplate?.subdivision ?? '');

      setLoading(false);
    }

    init();
  }, []);

  const handleSelectAll = (): void => {
    const sections = props.projectTitle ? titleService.getTitle()?.sections ?? [] : props.titleTemplate?.sections ?? [];

    if (selectAll) {
      if (props.mode === ManageMode.EXPORT)
        setSelectedSections(new Map(sections.filter((section: Section): boolean => section.name === 'Unclassified').map((section: Section): [string, string[]] => [section.id, []])));
      else
        setSelectedSections(new Map());
    } else {
      const sectionMap = new Map(sections.map((section: Section): [string, string[]] => [section.id, section.documents.map((document: Document): string => document.id)]));
      updatedDocuments.forEach((value: DocumentDto[], key: string): void => {
        value.forEach((document: DocumentDto): void => {
          sectionMap.get(key)?.push(document.id);
        });
      });

      setSelectedSections(sectionMap);
    }

    setSelectAll(!selectAll);
  }

  const handleSectionFilter = (sectionId: string, documents: DocumentDto[]): void => {
    const updatedMap = new Map(selectedSections);

    if (selectedSections.has(sectionId)) {
      updatedMap.delete(sectionId);
      setSelectedSections(updatedMap);
    } else {
      const documentIds: string[] = documents.map((document: DocumentDto): string => document.id);
      updatedMap.set(sectionId, documentIds);
    }

    setSelectedSections(updatedMap);
  }

  const isSelectedDocument = (sectionId: string, documentId: string): boolean => {
    if (selectedSections.has(sectionId)) {
      const documents = selectedSections.get(sectionId) ?? [];
      return documents.includes(documentId);
    }

    return false;
  }

  const handleDocumentFilter = (event: any, section: Section, documentId: string): void => {
    event.stopPropagation();

    const documentIds = selectedSections.get(section.id) as string[] ?? [];
    const updatedMap = new Map(selectedSections);

    const updatedDocumentIds = documentIds.includes(documentId) ? documentIds.filter((id: string): boolean => id !== documentId) : [...documentIds, documentId];
    if (updatedDocumentIds.length > 0)
      updatedMap.set(section.id, updatedDocumentIds);
    else if (section.name === 'Unclassified')
      updatedMap.set(section.id, []);
    else
      updatedMap.delete(section.id);

    setSelectedSections(updatedMap);
  }

  const handleDialogClose = (): void => {
    if(props.mode === ManageMode.TITLE_DOWNLOAD || props.mode === ManageMode.EXPORT)
      titleService.setPageOption(PageOption.DOCUMENTS);

    props.setOpen(false);
  }

  const handleSubmit = async (): Promise<void> => {
    switch (props.mode) {
    case ManageMode.DOWNLOAD:
      await handleDownload();
      break;

    case ManageMode.TITLE_DOWNLOAD:
      await handleTitleDownload();
      break;

    case ManageMode.CLONE:
      await handleClone();
      break;

    case ManageMode.UPDATE:
    case ManageMode.QUICK_UPDATE:
      await handleUpdate();
      break;

    case ManageMode.IMPORT:
      await handleImport();
      break;

    case ManageMode.EXPORT:
      await handleExport();
      break;

    default:
      break;
    }
  }

  const handleDownload = async (): Promise<void> => {
    setSubmitLoading(true);

    const titleTemplateRequest: TitleTemplateRequest = {
      sections: props.titleTemplate?.sections ?? [],
      templateName: props.titleTemplate?.name.replace(/[\/\\:*?"<>|]/g, '_') ?? '',
      tmsNumbers: props.titleTemplate?.tmsParcelNumbers.join(', ') ?? '',
      state: states.find((state: State) => state.id === props.titleTemplate?.stateID)?.shortName ?? '',
      county: counties.find((county: County) => county.id === props.titleTemplate?.countyID)?.name ?? '',
      address: props.titleTemplate?.address ?? '',
      subdivision: props.titleTemplate?.subdivision ?? '',
      templateType: templateType
    }

    await titleService.downloadTitle(selectedSections, [titleTemplateRequest], states, counties);
    setSubmitLoading(false);
    props.setOpen(false);
  }

  const handleClone = async (): Promise<void> => {
    setSubmitLoading(true);

    const cloneTitleTemplateDto: CloneTitleTemplateDto = {
      name: templateName ?? '',
      isPublic: props.titleTemplate?.isPublic ? false : templateType === TemplateType.PUBLIC
    };

    await titleTemplateService.cloneTitleTemplate(currentUser?.organizationID ?? '', props.titleTemplate?.id ?? '', cloneTitleTemplateDto);

    setSubmitLoading(false);
    props.setOpen(false);

    props.reloadCallback && props.reloadCallback();
  }

  const handleUpdate = async (): Promise<void> => {
    setSubmitLoading(true);

    const caceTemplate = titleTemplateService.getTitleTemplate();
    let sections: SectionDto[] = props.titleTemplate?.sections
      .filter((section: Section): boolean => Array.from(selectedSections.keys()).includes(section.id))
      .map((section: Section) => ({
        id: section.id,
        name: section.name,
        documents: section.documents
          .filter((document: Document) => selectedSections.get(section.id)?.includes(document.id))
          .map((document: Document) => ({
            id: document.id,
            name: document.name,
            url: document.url
          }))
      })) ?? [];

    if (sections) {
      let updatedSections = [...sections];
      const updatedDocumentsArray = Array.from(updatedDocuments.entries());

      for (let i = 0; i < updatedDocumentsArray.length; i++) {
        const [sectionId, documents] = updatedDocumentsArray[i] || [];
        let section = updatedSections.find((section) => section.id === sectionId);

        if (sectionId && documents && section) {
          const sectionDocuments = documents.filter((document: DocumentDto) => selectedSections.get(sectionId)?.includes(document.id));
          for (const sectionDocument of sectionDocuments)
            section.documents.push({id: '', name: sectionDocument.name, url: sectionDocument.url});
        }
      }

      sections = updatedSections;
    }

    const templatePayload: TitleTemplateDto = {
      name: templateName ?? '',
      titleID: props.titleTemplate?.titleID ?? '',
      address: templateAddress,
      stateID: templateState,
      countyID: templateCounty,
      subdivision: templateSubdivision,
      sectionIDs: [],
      documentIDs: [],
      sections: props.mode === ManageMode.QUICK_UPDATE ? sections : caceTemplate && caceTemplate.id === props.titleTemplate?.id ? caceTemplate.sections : props.titleTemplate?.sections ?? [],
      tmsParcelNumbers: templateTmsNumbers.split(','),
      isPublic: templateType === TemplateType.PUBLIC,
      isProjectTitle: true
    };

    await titleTemplateService.updateTitleTemplate(props.titleTemplate?.createdByOrganizationID ?? '', props.titleTemplate?.id ?? '', templatePayload);

    setSubmitLoading(false);
    props.setOpen(false);
    setUpdatedDocuments(new Map());
    setUpdatedSection(undefined);

    props.reloadCallback && props.reloadCallback();
  }

  const handleImport = async (): Promise<void> => {
    if (!props.importTitle && importTitle === '')
      toast.warning('Please select the title !!');
    else {
      setSubmitLoading(true);
      let sectionIds: string[] = [];
      let documentIds: string[] = [];

      selectedSections.forEach((value: string[], key: string): void => {
        sectionIds.push(key);
        documentIds.push(...value)}
      );

      if (sectionIds.length === 0 && documentIds.length === 0)
        toast.warning('Please select at least one document to import.');
      else {
        const body = {titleTemplateID: props.titleTemplate?.id, fields: ['sections'], sectionIDs: sectionIds, documentIDs: documentIds}
        const title = props.importTitle ?? titles.find((title: Title): boolean => title.id === importTitle);

        await titleTemplateService.importTitleTemplate(title?.createdByOrganizationID ?? '', title?.id ?? '', body);
        setSubmitLoading(false);
        props.setOpen(false);

        await router.navigate(`/organization/${title?.createdByOrganizationID}/title/${title?.id}`);
      }
    }
  }

  const handleTitleDownload = async (): Promise<void> => {
    setSubmitLoading(true);

    const titleTemplateRequest: TitleTemplateRequest = {
      sections: title?.sections ?? [],
      templateName: props.orderNumber.replace(/[\/\\:*?"<>|]/g, '_') ?? '',
      tmsNumbers: props.projectTitle?.tmsParcelNumbers.join(', ') ?? '',
      state: states.find((state: State) => state.id === props.projectTitle?.stateID ?? '')?.shortName ?? '',
      county: counties.find((county: County) => county.id === props.projectTitle?.countyID ?? '')?.name ?? '',
      address: props.projectTitle?.address ?? '',
      subdivision: props.projectTitle?.subdivision ?? '',
      templateType: templateType
    }

    await titleService.downloadTitle(selectedSections, [titleTemplateRequest], states, counties);
    setSubmitLoading(false);
    props.setOpen(false);
    titleService.setPageOption(PageOption.DOCUMENTS);
  }

  const handleExport = async (): Promise<void> => {
    setSubmitLoading(true);

    const templatePayload: TitleTemplateDto = {
      name: templateName ?? '',
      titleID: props.projectTitle?.id ?? '',
      address: props.projectTitle?.address ?? '',
      stateID: props.projectTitle?.stateID ?? '',
      countyID: props.projectTitle?.countyID ?? '',
      subdivision: templateSubdivision,
      sectionIDs: Array.from(selectedSections.keys()),
      documentIDs: Array.from(selectedSections.values()).flat(),
      sections: [],
      tmsParcelNumbers: props.projectTitle?.tmsParcelNumbers ?? [],
      isPublic: templateType === TemplateType.PUBLIC,
      isProjectTitle: true
    };
    await titleTemplateService.exportTitleTemplate(props.projectTitle?.createdByOrganizationID ?? '', templatePayload);

    setSubmitLoading(false);
    props.setOpen(false);
    titleService.setPageOption(PageOption.DOCUMENTS);
  }

  const handleUpload = (section: Section): void => {
    setUpdatedSection(section);

    if (!updatedDocuments.has(section.id))
      setUpdatedDocuments((prevState: Map<string, DocumentDto[]>): Map<string, DocumentDto[]> => prevState.set(section.id, []));
  }

  const handleProjectChange = async (event: SelectChangeEvent): Promise<void> => {
    setImportProject(event.target.value);
    setLoadingTitles(true);

    const project = projects.find((project: Project) => project.id === event.target.value);
    let titles = await titleService.getTitles(currentUser?.organizationID ?? '', project?.id ?? '');
    setTitles(titles);

    titles = titles.filter((title: Title): boolean => projectService.isPermittedTitle(title.id));
    setTitleOptions(titles.map((title: Title): DropdownOption => ({key: title.id, text: title.tmsParcelNumbers[0], value: title.id})));
    setLoadingTitles(false);
  }

  const renderTemplateInput = (index: number, value?: string): JSX.Element => {
    switch (index) {
    case 0:
      return (
        <FormControl fullWidth className={'template-input'}>
          <Select
            id={'select-template-type'}
            value={templateType}
            onChange={(event: SelectChangeEvent): void => setTemplateType(event.target.value === TemplateType.ORGANIZATION ? TemplateType.ORGANIZATION : TemplateType.PUBLIC)}>
            {
              Object.values(TemplateType).map((templateType: TemplateType): JSX.Element => <MenuItem key={templateType} value={templateType}>{templateType}</MenuItem>)
            }
          </Select>
        </FormControl>
      );

    case 1:
      return (
        <TextField
          id={'text-template-name'}
          fullWidth
          className={'template-input'}
          value={templateName}
          onChange={(event:  React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => setTemplateName(event.target.value)}/>
      );

    case 2:
      return (
        <FormControl fullWidth className={'template-input'}>
          <Select
            id={'select-template-county'}
            value={templateCounty}
            onChange={(event: SelectChangeEvent): void => setTemplateCounty(event.target.value)}>
            {
              countyOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
            }
          </Select>
        </FormControl>
      );

    case 3:
      return (
        <TextField
          id={'text-template-tms-numbers'}
          fullWidth
          className={'template-input'}
          value={templateTmsNumbers}
          onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => setTemplateTmsNumbers(event.target.value)}/>
      );

    case 4:
      return (
        <FormControl fullWidth className={'template-input'}>
          <Select
            id={'select-template-state'}
            value={templateState}
            onChange={(event: SelectChangeEvent): void => setTemplateState(event.target.value)}>
            {
              stateOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
            }
          </Select>
        </FormControl>
      );

    case 5:
      return (
        <TextField
          id={'text-template-address'}
          fullWidth
          className={'template-input'}
          value={templateAddress}
          onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => setTemplateAddress(event.target.value)}/>
      );

    case 6:
      return (
        <TextField
          id={'text-template-subdivision'}
          fullWidth
          className={'template-input'}
          value={templateSubdivision}
          onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => setTemplateSubdivision(event.target.value)}/>
      );

    default:
      return <Typography className={'template-data-value'}>{value ?? <Skeleton/>}</Typography>;
    }
  }

  const renderTemplateDetails = (data: any, index: number): JSX.Element => {
    return (
      <div key={index} className={'template-item template'}>
        <Typography className={'template-data-label template'}>{data.label}</Typography>
        {
          props.mode === ManageMode.CLONE
            ? index === 0 && !props.titleTemplate?.isPublic
              ? renderTemplateInput(index)
              : index === 1
                ? renderTemplateInput(index)
                : renderTemplateInput(-1, data.value)
            : props.mode === ManageMode.EXPORT
              ? index === 0 || index === 1 || index === 6
                ? renderTemplateInput(index)
                : renderTemplateInput(-1, data.value)
              : props.mode === ManageMode.UPDATE || props.mode === ManageMode.QUICK_UPDATE
                ? renderTemplateInput(index)
                : renderTemplateInput(-1, data.value)
        }
      </div>
    );
  }

  const renderSection = (section: Section, index: number): JSX.Element => {
    const hasDocuments = section.documents && section.documents.length > 0;
    const isExpanded = expandedSection === index;
    const isDisabled = section.name === 'Unclassified' && props.mode === ManageMode.EXPORT;
    const sections = props.titleTemplate ? props.titleTemplate.sections : title?.sections ?? [];

    return (
      <Accordion
        key={index}
        expanded={isExpanded}
        className={`template-list-item ${index % 2 === 0 ? 'even' : 'odd'} ${index === sections.length - 1 ? 'last' : ''} ${isExpanded ? 'parent' : ''}`}>
        <AccordionSummary expandIcon={ (hasDocuments || (updatedDocuments.get(section.id)?.length ?? 0) > 0) ? <ExpandMoreIcon onClick={(): void => setExpandedSection(expandedSection === index ? null : index)}/> : null}>
          <Box className={`checkbox-template-container parent template-checkbox ${isDisabled ? 'checkbox-disabled' : ''}`}>
            {
              props.mode !== ManageMode.VIEW && props.mode !== ManageMode.CLONE &&
                <Checkbox
                  id={`checkbox-section-${index}`}
                  checked={selectedSections.has(section.id)}
                  disabled={isDisabled}
                  onClick={(): void => handleSectionFilter(section.id, [...section.documents.map((document: Document) => ({
                    id: document.id,
                    name: document.name,
                    url: document.url
                  })), ...updatedDocuments.get(section.id) ?? []])}/>
            }
            <Typography className={'dialog-icon'}>{section.name}</Typography>
            {
              props.mode === ManageMode.QUICK_UPDATE &&
                <Box {...getRootProps()}>
                  <input hidden {...getInputProps()} accept={'.pdf'}/>
                  {
                    uploading && updatedSection && updatedSection.id === section.id
                      ? <Box className={'loading-container'}>
                          <CircularProgress size={16} className={'loading-icon'}/>
                          <span className={'progress'}>Uploading {documentIndex} of {documentCount}</span>
                        </Box>
                      : <CloudUpload
                    id={`btn-upload-section-${index}`}
                    onClick={(): void => handleUpload(section)}
                    titleAccess="Upload Document"
                    className={'document-upload-icon'}/>}
                </Box>
            }
          </Box>
        </AccordionSummary>
        {
          (hasDocuments || (updatedDocuments.get(section.id)?.length ?? 0) > 0) &&
            <AccordionDetails>
              {section.documents.map((document: Document): JSX.Element => renderDocument(section, document.id, document.name, document.url))}
              {updatedDocuments.get(section.id)?.map((document: DocumentDto): JSX.Element => renderDocument(section, document.name, document.name, document.url))}
            </AccordionDetails>
        }
      </Accordion>
    );
  }

  const renderDocument = (section: Section, documentId: string, documentName: string, documentUrl: string): JSX.Element => {
    return (
      <Box className={'template-list-item child'}>
        <Box className={'checkbox-template-container template-checkbox'} onClick={(): Window | null => window.open(documentUrl)}>
          {
            props.mode !== ManageMode.VIEW && props.mode !== ManageMode.CLONE &&
              <Checkbox
                id={`checkbox-document-${documentId}`}
                checked={isSelectedDocument(section.id, documentId)}
                onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => handleDocumentFilter(event, section, documentId)}/>
          }
          <PictureAsPdfIcon className={'zitles-icon checkbox-template-container icon'}/>
          <Typography className={'book-and-page-header'}>{documentName}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      {
        loading
          ? <LoadingComponent/>
          : <Dialog open={props.open}>
              <DialogTitle className={'zitles-dialog-title'}>
                <span>{props.mode === ManageMode.TITLE_DOWNLOAD ? 'Download Title' : props.mode + ' Title Template'}</span>
                <IconButton id={'btn-template-dialog-close'} onClick={handleDialogClose}>
                  <CloseIcon className={'dialog-icon'} />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <Paper elevation={0}>
                  <Accordion
                    elevation={0}
                    className={'template-dialog-card'}
                    expanded={templateDetailsAccordion}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon onClick={(): void => setTemplateDetailsAccordion(!templateDetailsAccordion)} className={'dialog-icon'}/>}>
                      <Typography className={'template-data-value accordion'}>Template Details</Typography>
                    </AccordionSummary>
                    <AccordionDetails>{ templateDetails.map(renderTemplateDetails) }
                    </AccordionDetails>
                  </Accordion>
                  {
                    props.mode !== ManageMode.UPDATE &&
                      <>
                        <Accordion
                          elevation={0}
                          className={'template-dialog-card'}
                          expanded={sectionAccordion}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon onClick={(): void => setSectionAccordion(!sectionAccordion)} className={'dialog-icon'}/>}>
                              <Typography className={'template-data-value accordion'}>Sections and Documents</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              {
                                props.mode !== ManageMode.VIEW && props.mode !== ManageMode.CLONE &&
                                  <Box className={'checkbox-template-container header template-checkbox'}>
                                    <Checkbox
                                      id={'checkbox-template-select-all'}
                                      checked={selectAll}
                                      onClick={handleSelectAll}/>
                                    <Typography className={'checkbox-template-container title'}>{selectAll ? 'Deselect All' : 'Select All'}</Typography>
                                  </Box>
                              }
                              {props.titleTemplate ? props.titleTemplate.sections.map(renderSection) : title?.sections.map(renderSection)}
                            </AccordionDetails>
                        </Accordion>
                        {
                          props.mode === ManageMode.IMPORT && !props.importTitle &&
                            <Accordion
                              elevation={0}
                              className={`template-dialog-card ${!importAccordion ? ' list last' : ''}`}
                              expanded={importAccordion}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon onClick={(): void => setImportAccordion(!importAccordion)}/>}>
                                <Typography className={'template-data-value'}>Import To</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <FormControl fullWidth className={'import-order-dropdown'}>
                                  <InputLabel>Import File</InputLabel>
                                  <Select
                                    id={'select-import-project'}
                                    value={importProject}
                                    label={'Import File'}
                                    onChange={handleProjectChange}>
                                    {
                                      projectOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                                    }
                                  </Select>
                                </FormControl>
                                {
                                  importProject &&
                                    <FormControl fullWidth className={'import-order-dropdown'}>
                                    <InputLabel>Import Title</InputLabel>
                                    <Select
                                      id={'select-import-title'}
                                      value={importTitle}
                                      label={'Import Title'}
                                      onChange={(event: SelectChangeEvent): void => setImportTitle(event.target.value)}>
                                      {
                                        titleOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                                      }
                                    </Select>
                                  </FormControl>
                                }
                              </AccordionDetails>
                            </Accordion>
                        }
                      </>
                  }
                  {
                    loadingTitles &&
                      <LoadingComponent/>
                  }
                </Paper>
              </DialogContent>
              {
                props.mode !== ManageMode.VIEW &&
                  <DialogActions className={'action-btn-container'}>
                    <Button
                      id={'btn-template-dialog-cancel'}
                      disableTouchRipple
                      onClick={handleDialogClose}
                      className={'zitles-button cancel'}>
                      Cancel
                    </Button>
                    <Button
                      id={'btn-template-dialog-submit'}
                      disabled={submitLoading}
                      className={`zitles-button template submit ${submitLoading ? 'disable' : ''}`}
                      onClick={handleSubmit}
                      startIcon={submitLoading ? <CircularProgress size={14} color='inherit'/> : null}>
                      {submitLoading ? submitButtonMap.get(props.mode) : props.mode === ManageMode.TITLE_DOWNLOAD ? 'Download' : props.mode}
                    </Button>
                  </DialogActions>
              }
            </Dialog>
      }
    </>
  );
}

export default observer(TemplateDialog);
